/**
 * Sharing a route out of the app.
 *
 * There's no native share plugin wired into this build, so the reliable path is
 * a GPX file, the format every plotter (Navionics, iSailor, OpenCPN, Garmin)
 * reads. If the webview/browser exposes the Web Share API with file support we
 * try that first, so a phone can hand the GPX straight to email, messages or
 * another nav app. Where that isn't available we fall back to saving the GPX as
 * a download, the same mechanism the legacy route panel used. Either way the
 * skipper ends up with a standard GPX they can open anywhere.
 */
import { routeToGpx, type Route } from '@/routing';

function gpxFilename(route: Route): string {
  return `${route.name.replace(/[^a-z0-9-_]+/gi, '_') || 'route'}.gpx`;
}

function downloadGpx(xml: string, filename: string): void {
  const blob = new Blob([xml], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Give the browser a moment to start the download before releasing the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Share a route as GPX. Returns how it went: 'shared' via the native sheet, or
 * 'downloaded' as a saved file. Throws only if the native share sheet itself
 * errors (a user cancel shows up as an AbortError the caller can ignore).
 */
export async function shareRoute(route: Route): Promise<'shared' | 'downloaded'> {
  const xml = routeToGpx(route);
  const filename = gpxFilename(route);

  const nav = navigator as Navigator & { canShare?: (data: unknown) => boolean };
  if (typeof navigator.share === 'function' && typeof File !== 'undefined') {
    const file = new File([xml], filename, { type: 'application/gpx+xml' });
    // canShare tells us whether files are allowed; if it's missing we optimistically try.
    if (!nav.canShare || nav.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: route.name });
      return 'shared';
    }
  }

  downloadGpx(xml, filename);
  return 'downloaded';
}
